import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";
import { useOrganizationBranch } from "../../context/OrganizationBranchContext";
import { ThemeContext } from "../../context/ThemeContext";
import { Dropdown, Badge } from "react-bootstrap";
import {
  faUser,
  faGear,
  faSignOutAlt,
  faMoon,
  faSun,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BsBell, BsEnvelope, BsCheck2Square } from "react-icons/bs";
import "./Header.css";
import { useSubscription } from "../../hooks/useSubscription";
import usePendingApprovals from "../../hooks/usePendingApprovals";
import useNotifications from "../../hooks/useNotifications";
import useUnreadMessages from "../../hooks/useUnreadMessages";

const timeAgo = (iso) => {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const Header = () => {
  const { companyInfo } = useCompany();
  const { logout, user } = useAuth();
  const {
    currentOrganization,
    currentBranch,
    branches,
    organizationMode,
    getBranchDisplayName,
    getOrganizationDisplayName,
  } = useOrganizationBranch();
  const { getSubscriptionPlan, SUBSCRIPTION_PLANS } = useSubscription();
  const { count: pendingApprovalCount } = usePendingApprovals();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const navigate = useNavigate();

  // Logic to determine what to display in header
  const getHeaderDisplayInfo = () => {
    console.log("Header Display Logic Debug:", {
      "User Email": user?.email || "None",
      "User Full Name": user?.fullName || "None",
      "Branches Count": branches?.length || 0,
      "Current Branch": currentBranch?.name || "None",
      "Current Organization": currentOrganization?.name || "None",
      "Organization Mode": organizationMode,
    });

    // If we have multiple branches and a current branch is selected, show branch info
    if (branches && branches.length > 1 && currentBranch) {
      console.log("🔷 Header showing BRANCH context:", currentBranch.name);
      return {
        logo:
          user?.profilePicture ||
          currentBranch.logo ||
          currentOrganization?.logo ||
          companyInfo.logo,
        name: user?.fullName || getBranchDisplayName(currentBranch),
        email:
          user?.email ||
          currentBranch.email ||
          currentBranch.managerEmail ||
          currentOrganization?.email ||
          companyInfo.email,
        type: "branch",
      };
    }

    // If we have exactly one branch, still show organization context
    if (branches && branches.length === 1 && currentOrganization) {
      console.log(
        "🏢 Header showing ORGANIZATION context (single branch):",
        currentOrganization.name
      );
      return {
        logo:
          user?.profilePicture || currentOrganization.logo || companyInfo.logo,
        name:
          user?.fullName ||
          getOrganizationDisplayName(currentOrganization, currentBranch),
        email: user?.email || currentOrganization.email || companyInfo.email,
        type: "organization",
      };
    }

    // If we have an organization (no branches or general case), show organization info
    if (currentOrganization) {
      console.log(
        "🏢 Header showing ORGANIZATION context:",
        currentOrganization.name
      );
      return {
        logo:
          user?.profilePicture || currentOrganization.logo || companyInfo.logo,
        name:
          user?.fullName ||
          getOrganizationDisplayName(currentOrganization, currentBranch),
        email: user?.email || currentOrganization.email || companyInfo.email,
        type: "organization",
      };
    }

    // Fallback to user info if available, otherwise company info
    if (user) {
      console.log("👤 Header showing USER context:", user.email);
      return {
        logo: user.profilePicture,
        name: user.fullName || user.email,
        email: user.email,
        type: "user",
      };
    }

    // Fallback to company info
    console.log("📋 Header showing COMPANY fallback context");
    return {
      logo: companyInfo.logo,
      name: companyInfo.name || "Account",
      email: companyInfo.email || "superuser@admin.com",
      type: "company",
    };
  };

  // Memoize header display info to avoid unnecessary recalculations
  const headerInfo = React.useMemo(() => {
    return getHeaderDisplayInfo();
  }, [
    user,
    currentOrganization,
    currentBranch,
    branches,
    companyInfo,
    getBranchDisplayName,
    getOrganizationDisplayName,
    darkMode,
  ]);

  // Check if sidebar is collapsed on mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState) {
      setSidebarCollapsed(JSON.parse(savedState));
    }

    // Listen for changes to localStorage
    const handleStorageChange = (e) => {
      if (e.key === "sidebarCollapsed") {
        setSidebarCollapsed(JSON.parse(e.newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const {
    unread: unreadCount,
    items: notifications,
    loading: notificationsLoading,
    error: notificationsError,
    loadList: loadNotifications,
    markRead,
    markAllRead,
    remove: removeNotification,
  } = useNotifications(!!user);

  const { unread: unreadMessages } = useUnreadMessages(!!user);

  const openNotification = (n) => {
    if (!n.readAt) markRead(n._id);
    setShowNotifications(false);
    if (n.link) navigate(n.link);
  };

  const getInitials = (name) => {
    const nameToUse = user?.fullName || name;
    if (!nameToUse) return "A";

    const parts = nameToUse.split(" ");
    if (parts.length > 1) {
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    } else {
      return nameToUse.charAt(0).toUpperCase();
    }
  };

  const handleProfileClick = () => {
    navigate("/profile-settings");
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    // Use AuthContext logout method which properly clears all state
    logout();
    // Clear any additional localStorage items
    localStorage.removeItem("authToken");
    localStorage.removeItem("sidebarCollapsed");
    localStorage.removeItem("currentOrganization");
    localStorage.removeItem("currentBranch");
    // Discard any super-admin impersonation trail so a next login starts
    // clean.
    localStorage.removeItem("impersonation");
    localStorage.removeItem("saOwnerToken");
    localStorage.removeItem("saOwnerUser");
    // Clear axios headers
    delete require("../../api/axiosConfig").default.defaults.headers.common[
      "Authorization"
    ];
    // Navigate to login
    navigate("/login");
  };
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    // Apply dark mode to the body or a specific container
    document.body.classList.toggle("dark-mode");
  };
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));

    // Dispatch event to notify other components
    window.dispatchEvent(new Event("storage"));
  };

  // Update document theme when dark mode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
    }
  }, [darkMode]);

  // Listen for header context updates from other components
  useEffect(() => {
    const handleHeaderContextUpdate = () => {
      console.log("🔄 Header context update triggered");
      // The useMemo dependency array will automatically trigger a re-render
    };

    window.addEventListener("headerContextUpdated", handleHeaderContextUpdate);
    window.addEventListener("branchesUpdated", handleHeaderContextUpdate);
    window.addEventListener("organizationsUpdated", handleHeaderContextUpdate);

    return () => {
      window.removeEventListener(
        "headerContextUpdated",
        handleHeaderContextUpdate
      );
      window.removeEventListener("branchesUpdated", handleHeaderContextUpdate);
      window.removeEventListener(
        "organizationsUpdated",
        handleHeaderContextUpdate
      );
    };
  }, []);

  // App Settings used to be an enterprise-plan gate. Plan gating was
  // removed from the app — access is now managed by the SA-side page
  // roles map — so every authenticated user can open App Settings.
  const canAccessAppSettings = () => true;

  return (
    <header className="app-header">
      <div className="header-left">
        <div className="sidebar-toggle-btn" onClick={toggleSidebar}>
          <FontAwesomeIcon
            icon={sidebarCollapsed ? faAngleRight : faAngleLeft}
          />
        </div>
      </div>

      <div className="header-right">
        {/* Phase 28f: pending-for-me approvals badge */}
        <div
          className="header-icon-btn"
          title="Pending approvals for me"
          onClick={() => navigate("/approvals")}
          style={{ cursor: "pointer", marginRight: 8 }}
        >
          <div className="icon-with-badge">
            <BsCheck2Square className="header-icon" />
            {pendingApprovalCount > 0 && (
              <Badge bg="danger" pill className="notification-badge">
                {pendingApprovalCount}
              </Badge>
            )}
          </div>
        </div>

        {/* Theme toggle - COMMENTED OUT FOR NOW */}
        {/* <div className="theme-toggle" onClick={toggleDarkMode}>
          <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
        </div> */}

        {/* Notifications */}
        <Dropdown
          show={showNotifications}
          onToggle={(next) => {
            setShowNotifications(next);
            if (next) loadNotifications();
          }}
          align="end"
        >
          <Dropdown.Toggle as="div" className="header-icon-btn" aria-label="Notifications">
            <div className="icon-with-badge">
              <BsBell className="header-icon" />
              {unreadCount > 0 && (
                <Badge bg="danger" pill className="notification-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              )}
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu className="notifications-dropdown">
            <div className="notifications-header">
              <h6>Notifications</h6>
              {unreadCount > 0 && (
                <small
                  className="text-primary"
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={markAllRead}
                >
                  Mark all as read
                </small>
              )}
            </div>
            <Dropdown.Divider />

            {notificationsError ? (
              <div className="empty-notifications text-danger">{notificationsError}</div>
            ) : notificationsLoading && notifications.length === 0 ? (
              <div className="empty-notifications">Loading…</div>
            ) : notifications.length > 0 ? (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {notifications.map((n) => (
                  <Dropdown.Item
                    key={n._id}
                    as="div"
                    className={`notification-item ${!n.readAt ? "unread" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => openNotification(n)}
                  >
                    <div className="notification-content">
                      <div className="notification-text" style={{ fontWeight: n.readAt ? 400 : 600 }}>
                        {n.title}
                      </div>
                      {n.body && <small className="d-block text-muted">{n.body}</small>}
                      <small className="notification-time">{timeAgo(n.createdAt)}</small>
                    </div>
                    <span
                      role="button"
                      aria-label="Dismiss notification"
                      title="Dismiss"
                      style={{ marginLeft: 8, opacity: 0.6, cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(n._id);
                      }}
                    >
                      ×
                    </span>
                  </Dropdown.Item>
                ))}
              </div>
            ) : (
              <div className="empty-notifications">No notifications</div>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {/* Messages */}
        <div
          className="header-icon-btn"
          title="Messages"
          aria-label="Messages"
          onClick={() => navigate("/messages")}
          style={{ cursor: "pointer" }}
        >
          <div className="icon-with-badge">
            <BsEnvelope className="header-icon" />
            {unreadMessages > 0 && (
              <Badge bg="danger" pill className="notification-badge">
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </Badge>
            )}
          </div>
        </div>

        {/* Updated Profile Dropdown */}
        <Dropdown
          show={isDropdownOpen}
          onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
          align="end"
        >
          <Dropdown.Toggle as="div" className="profile-toggle">
            {headerInfo.logo ? (
              <div className="profile-image-container">
                <img
                  src={headerInfo.logo}
                  alt="Profile Logo"
                  className="header-profile-image"
                />
                {headerInfo.type === "branch" && (
                  <div
                    className="context-indicator branch-indicator"
                    title="Branch Context"
                  >
                    📍
                  </div>
                )}
                {headerInfo.type === "organization" && (
                  <div
                    className="context-indicator org-indicator"
                    title="Organization Context"
                  >
                    🏢
                  </div>
                )}
              </div>
            ) : (
              <div className="profile-initial-container">
                <div className="header-profile-initial">
                  {getInitials(headerInfo.name)}
                </div>
                {headerInfo.type === "branch" && (
                  <div
                    className="context-indicator branch-indicator"
                    title="Branch Context"
                  >
                    📍
                  </div>
                )}
                {headerInfo.type === "organization" && (
                  <div
                    className="context-indicator org-indicator"
                    title="Organization Context"
                  >
                    🏢
                  </div>
                )}
              </div>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu
            className={`profile-dropdown ${isDropdownOpen ? "show" : ""}`}
          >
            <div className="profile-dropdown-header animate-item">
              <div className="profile-header-content">
                <div className="profile-info">
                  <strong>{user?.fullName || "Account"}</strong>
                  <span>{user?.email || "superuser@admin.com"}</span>
                  {headerInfo.type === "branch" && (
                    <small className="text-info">
                      📍 Branch: {headerInfo.name}
                    </small>
                  )}
                  {headerInfo.type === "organization" && (
                    <small className="text-success">
                      🏢 Organization:{" "}
                      {currentOrganization?.name || headerInfo.name}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="profile-menu-items">
              <Dropdown.Item
                className="profile-menu-item animate-item"
                onClick={handleProfileClick}
              >
                <div className="menu-icon">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="menu-content">
                  <span>Profile Settings</span>
                  <small>Manage your account</small>
                </div>
              </Dropdown.Item>

              {canAccessAppSettings() && (
                <Dropdown.Item
                  className="profile-menu-item animate-item"
                  onClick={() => {
                    navigate("/app-settings");
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="menu-icon settings-icon">
                    <FontAwesomeIcon icon={faGear} />
                  </div>
                  <div className="menu-content">
                    <span>App Settings</span>
                    <small>Configure app preferences</small>
                  </div>
                </Dropdown.Item>
              )}

              {user?.role === "super_admin" && (
                <Dropdown.Item
                  className="profile-menu-item animate-item"
                  onClick={() => {
                    navigate("/super-admin");
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="menu-icon">
                    <FontAwesomeIcon icon={faGear} />
                  </div>
                  <div className="menu-content">
                    <span>Super Admin</span>
                    <small>Site owner console</small>
                  </div>
                </Dropdown.Item>
              )}

              <Dropdown.Item
                className="profile-menu-item logout animate-item"
                onClick={handleLogout}
              >
                <div className="menu-icon">
                  <FontAwesomeIcon icon={faSignOutAlt} />
                </div>
                <div className="menu-content">
                  <span>Logout</span>
                  <small>Sign out of your account</small>
                </div>
              </Dropdown.Item>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
